import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo, useState } from "react";

import { AMBIENT_SCRIBE_PROCESSING_EVENTS } from "constants/feature-flags";
import {
  AMBIENT_SCRIBE_STATUS_CANCELLED,
  AMBIENT_SCRIBE_STATUS_COMPLETED,
  AMBIENT_SCRIBE_STATUS_FAILED,
  AMBIENT_SCRIBE_STATUS_TRANSCRIBED,
  AMBIENT_SCRIBE_STATUS_UPLOADED
} from "features/medical-scribe/medical-scribe-constants";
import useScribeSalesCoachTab from "features/sales-coach/embedded/use-scribe-sales-coach-tab.hook";
import type { AmbientScribe } from "services/types/ambient-scribe.type";

import useOnDepsChange from "./use-on-deps-change.hook";

export const DETAIL_TAB_DISPLAY_ORDER = [
  "soap",
  "context",
  "transcript",
  "summary",
  "avs",
  "debrief",
  "salesCoach",
  "events"
] as const;

export type DetailTabId = (typeof DETAIL_TAB_DISPLAY_ORDER)[number];

type TabRules = {
  shouldShowTranscriptTab: boolean;
  shouldShowContextTab: boolean;
  isProcessingEventsEnabled: boolean;
  /** The letter tab needs a transcript; audiology mode keeps the dialog on its report summary. */
  showsAfterVisitSummary: boolean;
  isDebriefEligible: boolean;
  isSalesCoachTabVisible: boolean;
};

/** The tabs a loaded scribe offers, in the order the first one is chosen from. */
export function getAvailableDetailTabs(
  remote: AmbientScribe | undefined,
  rules: TabRules
): string[] {
  if (!remote) return [];
  const tabs: string[] = [];
  if (remote.has_transcription) {
    tabs.push("soap");
    if (rules.shouldShowTranscriptTab) tabs.push("transcript");
    if (rules.shouldShowContextTab) tabs.push("context");
    tabs.push("summary");
    if (rules.showsAfterVisitSummary) tabs.push("avs");
    if (rules.isProcessingEventsEnabled) tabs.push("events");
  } else if (rules.isProcessingEventsEnabled) {
    if (rules.shouldShowContextTab) tabs.push("context");
    tabs.push("events");
  }
  if (rules.isDebriefEligible) tabs.push("debrief");
  // After the others, like `debrief`, so it can never become the first tab, which is what the
  // fallback reads.
  if (rules.isSalesCoachTabVisible) tabs.push("salesCoach");
  return tabs;
}

/** Identifies the recording the details show, so tab state can start over when it changes. */
export function getScribeDetailsKey(
  remoteMedicalScribeId: number | undefined,
  localId: string | undefined
) {
  return `${remoteMedicalScribeId ?? ""}:${localId ?? ""}`;
}

/** Which tabs this recording's status allows, before any feature switch is considered. */
function getStatusTabRules(remote: AmbientScribe | undefined) {
  const status = remote?.status;
  return {
    shouldShowTranscriptTab:
      Boolean(remote?.has_transcription) ||
      (status !== AMBIENT_SCRIBE_STATUS_UPLOADED &&
        status !== AMBIENT_SCRIBE_STATUS_TRANSCRIBED),
    shouldShowContextTab:
      status !== AMBIENT_SCRIBE_STATUS_CANCELLED &&
      status !== AMBIENT_SCRIBE_STATUS_FAILED,
    isCompletedStatus: status === AMBIENT_SCRIBE_STATUS_COMPLETED
  };
}

type OpenTabParams = {
  scribeKey: string;
  remoteId: AmbientScribe["id"] | undefined;
  /** The recording is loaded and no request is in flight. */
  isSettled: boolean;
  availableDetailTabs: string[];
  shouldShowTranscriptTab: boolean;
  initialTab?: DetailTabOptions["initialTab"];
};

/**
 * The open tab, adjusted while rendering, never in an effect: a parent told about the tab from
 * an effect learns it a frame late, and a chain of effects each re-renders the dialog.
 */
function useOpenDetailTab({
  scribeKey,
  remoteId,
  isSettled,
  availableDetailTabs,
  shouldShowTranscriptTab,
  initialTab
}: OpenTabParams) {
  const fallbackTab = availableDetailTabs[0] ?? "events";
  const hasTabs = availableDetailTabs.length > 0;

  const [tabView, setTabView] = useState<string>("transcript");
  useOnDepsChange([scribeKey], () => setTabView("transcript"));

  // The first tab is chosen once per recording, after its read settles, and NOT again on every
  // 2500 ms poll — a better tab appearing later must not move someone off the one they are on.
  const [isInitialized, setIsInitialized] = useState(false);
  useOnDepsChange([scribeKey, remoteId], () => setIsInitialized(false));
  if (!isInitialized && isSettled && hasTabs) {
    setIsInitialized(true);
    // `initialTab` is only ever a FIRST choice, never the fallback: the branch below settles
    // because the fallback is never "transcript", and a missing transcript would loop if it were.
    setTabView(
      initialTab && availableDetailTabs.includes(initialTab)
        ? initialTab
        : fallbackTab
    );
  }

  // The open tab went away (e.g. the transcript tab during the streaming → uploaded transition):
  // move to the first tab, and stay there. The fallback is never "transcript", so this settles.
  const isOpenTabGone = hasTabs && !availableDetailTabs.includes(tabView);
  const isTranscriptGone = tabView === "transcript" && !shouldShowTranscriptTab;
  if (tabView !== fallbackTab && (isOpenTabGone || isTranscriptGone)) {
    setTabView(fallbackTab);
  }

  return { tabView: isOpenTabGone ? fallbackTab : tabView, setTabView };
}

/**
 * What a host can ask of the tabs. Both are unset everywhere except the Sales Coach review, which
 * opens this dialog ON TOP of itself: it hides the Sales Coach tab (a second `ReviewContent` would
 * break the review's in-page jumps, whose DOM ids are global) and opens on the transcript, which
 * is what a reader checks a review against.
 */
export type DetailTabOptions = {
  hideSalesCoachTab?: boolean;
  /** Opened first when the recording has it; otherwise the first tab, as without it. */
  initialTab?: "transcript";
};

type ScribeDetailTabsParams = DetailTabOptions & {
  remote: AmbientScribe | undefined;
  isLoading: boolean;
  isFetching: boolean;
  /** Which recording is shown. A new one starts over, as a remounted details pane would. */
  scribeKey: string;
  isAudiologyMode: boolean;
  isAvsEnabled: boolean;
  isConsultDebriefEnabled: boolean;
};

/**
 * Which detail tab is open, and which exist. The scribe details own this unless a parent needs to
 * know the open tab too (Insert Scribe labels and enables its button from it); then the parent
 * calls this hook and hands the result down as `tabs`.
 */
export default function useScribeDetailTabs({
  remote,
  isLoading,
  isFetching,
  scribeKey,
  isAudiologyMode,
  isAvsEnabled,
  isConsultDebriefEnabled,
  hideSalesCoachTab = false,
  initialTab
}: ScribeDetailTabsParams) {
  const flags = useFlags();
  const isProcessingEventsEnabled = Boolean(
    flags[AMBIENT_SCRIBE_PROCESSING_EVENTS]
  );
  const { shouldShowTranscriptTab, shouldShowContextTab, isCompletedStatus } =
    getStatusTabRules(remote);

  // Eligibility, not existence, gates the tab (D2 revised, ENG-1706): a scribe with no debrief
  // yet still gets the tab, showing a "Generate Debrief" CTA instead of content. COMPLETED is
  // the same status the backend's own `enqueue_debrief_for_scribe` requires, so this never
  // offers a CTA that would just be rejected server-side.
  const isDebriefEligible = isCompletedStatus && isConsultDebriefEnabled;

  // The Sales Coach review for this recording, if this user may see it. No Analyse here — a
  // first analysis belongs on the appointment page; Re-analyse lives inside the review (PRD D25).
  // Eligibility, the role gate and the once-shown latch all live in the hook.
  const { isVisible: isSalesCoachTabVisible, reviewId: salesCoachReviewId } =
    useScribeSalesCoachTab({
      scribeId: remote?.id != null ? Number(remote.id) : undefined,
      isEligible: isCompletedStatus && !isAudiologyMode && !hideSalesCoachTab
    });

  const availableDetailTabs = useMemo(
    () =>
      getAvailableDetailTabs(remote, {
        shouldShowTranscriptTab,
        shouldShowContextTab,
        isProcessingEventsEnabled,
        showsAfterVisitSummary: isAvsEnabled && !isAudiologyMode,
        isDebriefEligible,
        isSalesCoachTabVisible
      }),
    [
      remote,
      shouldShowTranscriptTab,
      shouldShowContextTab,
      isProcessingEventsEnabled,
      isAvsEnabled,
      isAudiologyMode,
      isDebriefEligible,
      isSalesCoachTabVisible
    ]
  );

  const { tabView, setTabView } = useOpenDetailTab({
    scribeKey,
    remoteId: remote?.id,
    isSettled: Boolean(remote) && !isLoading && !isFetching,
    availableDetailTabs,
    shouldShowTranscriptTab,
    initialTab
  });

  const visibleDetailTabs = useMemo(
    () =>
      DETAIL_TAB_DISPLAY_ORDER.filter((id) => availableDetailTabs.includes(id)),
    [availableDetailTabs]
  );

  return {
    tabView,
    setTabView,
    availableDetailTabs,
    visibleDetailTabs,
    shouldShowContextTab,
    salesCoachReviewId
  };
}

export type ScribeDetailTabs = ReturnType<typeof useScribeDetailTabs>;
